/**
 * ============================================================================
 * NAVIGATION ORCHESTRATOR
 * ============================================================================
 *
 * SINGLE SOURCE OF TRUTH for:
 *   1) Left navbar completion / visibility rules
 *   2) Cache -> KRA fallback resolution
 *   3) getStatus (StageDetail/GetStatusV1) driven navigation decisions
 *
 * Reference implementation: WelcomeBack.tsx ("Continue" flow). Its behavior
 * is considered correct/authoritative. Every function here is a verbatim
 * (or bug-fixed, explicitly marked "BUGFIX") extraction of logic that used
 * to be duplicated across WelcomeBack.tsx, StatusContinue.tsx and
 * PANInfo.tsx.
 *
 * If you need to debug: "why did the navbar look wrong" / "why didn't KRA
 * get called" / "why did the app redirect me to X instead of Y" -- this is
 * the one file to open.
 * ============================================================================
 */

import GlobalStore from "../store/globalStore";
import NriAddressStore from "../store/nriAddressStore";
import {
  ILeftNavBarArray,
  dashboardUrl,
  containerUrl,
  statusUrl,
  leftNavBarArray,
  ipvNavBarObj,
  dematNavBarObj,
  authenticateNavBarObj,
  hasNavBarEntry,
} from "../utils/constants";
import { AGetCacheData } from "./registration/continueApiService";
import { AGetKraDetails } from "./registration/panDobApiService";
import {
  ALeadFetchApi,
  AResetAccount,
} from "./registration/registrationApiService";
import moment from "moment";

/* ============================================================================
 * SECTION 1 - LEFT NAVBAR
 *
 * Rule: GlobalStore.setLeftNavBarState() is still the only allowed mutator
 * (enforced in globalStore.ts). Every function below always derives its
 * next state from a COPY of an array (either GlobalStore.leftNavBarState or
 * the pristine leftNavBarArray constant) and never mutates a shared
 * reference in place. This fixes a real bug where several PANInfo.tsx call
 * sites did `let filterLeftBar = leftNavBarArray; filterLeftBar.splice(...)`
 * which permanently mutated the shared module-level constant, corrupting
 * the "pristine" navbar list for the rest of the session.
 * ==========================================================================*/

/**
 * Marks a navbar step complete (and, for KRA/KYC/Digi/OTC clients, hides the
 * legacy standalone "Personal Documents" step). Ported verbatim from
 * globalService.ts (WelcomeBack's reference behavior).
 */
export function completeModuleStatus(navUrl: string): void {
  let moduleArr: Array<ILeftNavBarArray> = GlobalStore.leftNavBarState;
  const clientPartialDetails_ao = JSON.parse(
    window.localStorage.getItem("clientPartialDetails_ao") || "{}"
  );
  if (
    GlobalStore.isKRA ||
    clientPartialDetails_ao.isKRA ||
    GlobalStore.isKYC ||
    clientPartialDetails_ao.isKYC ||
    GlobalStore.isDigiLocker ||
    clientPartialDetails_ao.isDigiLocker
  ) {
    moduleArr = GlobalStore.leftNavBarState.filter(
      (item) => item.name !== "Personal Documents"
    );
  }
  if (GlobalStore.isOTCFlow) {
    moduleArr = GlobalStore.leftNavBarState.filter(
      (item) => item.name !== "Personal Documents"
    );
  }
  // Work on a shallow copy so we never mutate GlobalStore.leftNavBarState's
  // array/object references in place before calling the setter.
  moduleArr = moduleArr.map((item) => ({ ...item }));
  for (let element of moduleArr) {
    if (element.navUrl === navUrl) {
      if (navUrl == "/esign") {
        if (
          GlobalStore.stageStatus.isesigncompleted &&
          GlobalStore.stageStatus.iskraesigncompleted
        ) {
          element.isComplete = true;
          continue;
        } else {
          element.isComplete = false;
          continue;
        }
      }
      element.isComplete = true;
      break;
    }
  }
  GlobalStore.setLeftNavBarState(moduleArr);
}

/** Returns the next incomplete stage's navUrl. Ported verbatim from globalService.ts. */
export function incompleteStageNavigation(currentPage?: string): string {
  let returnUrl: string = dashboardUrl.eSign;
  const clientPartialDetails_ao = JSON.parse(
    window.localStorage.getItem("clientPartialDetails_ao") || "{}"
  );

  if (
    (GlobalStore.applicationID || clientPartialDetails_ao.applicationId) &&
    !GlobalStore.isDigiLocker
  ) {
    let filteredLeftNavBar;
    if (GlobalStore.isOTCFlow) {
      filteredLeftNavBar = GlobalStore.leftNavBarState.filter(
        (item) => item.name !== "Personal Documents"
      );
    }
    if (
      GlobalStore.isKRA ||
      clientPartialDetails_ao.isKRA ||
      GlobalStore.isKYC ||
      clientPartialDetails_ao.isKYC
    ) {
      filteredLeftNavBar = GlobalStore.leftNavBarState.filter(
        (item) => item.name !== "Personal Documents"
      );
      if (filteredLeftNavBar && filteredLeftNavBar.length > 0) {
        GlobalStore.setLeftNavBarState(filteredLeftNavBar);
      }
    }
  }

  const stages = [...GlobalStore.leftNavBarState].sort(
    (a, b) => a.priority - b.priority
  );
  const allCompleted = stages.every((item) => item.isComplete);

  if (allCompleted) {
    const lastStage = stages[stages.length - 1];
    if (lastStage.navUrl !== currentPage) {
      returnUrl = lastStage.navUrl;
    }
    return returnUrl;
  }

  for (let i = 0; i < stages.length; i++) {
    if (!stages[i].isComplete && stages[i].navUrl !== currentPage) {
      returnUrl = stages[i].navUrl;
      break;
    }
  }

  return returnUrl;
}

/**
 * Maps GlobalStore.stageStatus (populated via setStageData from a getStatus
 * response) onto navbar completion, and conditionally injects the IPV /
 * Demat tiles. Ported verbatim from ProfileNavBar.tsx (WelcomeBack's
 * reference behavior).
 */
export function checkModuleCompleteness(
  isUploadDocumentsCompleted: boolean,
  dematAccountFound?: any,
  addressAccountFoundLocal?: any
): void {
  if (GlobalStore.stageStatus.isaddressstagecompleted) {
    completeModuleStatus(dashboardUrl.personalIfo);
  }
  if (
    GlobalStore.stageStatus.isprofiledetailscompleted &&
    GlobalStore.stageStatus.ispersonaldetailscompleted
  )
    completeModuleStatus(dashboardUrl.profile);
  if (isUploadDocumentsCompleted) completeModuleStatus(dashboardUrl.doc);
  if (GlobalStore.stageStatus.isbankdetailscompleted)
    completeModuleStatus(dashboardUrl.bank);
  if (
    GlobalStore.stageStatus.isbrokeragesegmentcompleted &&
    GlobalStore.stageStatus.isbrokerageschemecompleted
  )
    completeModuleStatus(dashboardUrl.plan);
  if (GlobalStore.stageStatus.isipvarificationcompleted) {
    if (GlobalStore.leftNavBarState.length === leftNavBarArray.length) {
      if (!hasNavBarEntry(GlobalStore.leftNavBarState, ipvNavBarObj.navUrl))
        GlobalStore.setLeftNavBarState((prevState) => [
          ...prevState,
          ipvNavBarObj,
        ]);
    }
    completeModuleStatus(dashboardUrl.ipv);
  }
  if (
    GlobalStore.stageStatus.isdpcompleted !== null &&
    GlobalStore?.stageStatus?.isdpcompleted !== undefined &&
    GlobalStore?.stageStatus?.accountcategory == 2 &&
    GlobalStore?.stageStatus?.isdpcompleted
  ) {
    if (!hasNavBarEntry(GlobalStore.leftNavBarState, dematNavBarObj.navUrl))
      GlobalStore.setLeftNavBarState((prevState) => [
        ...prevState,
        dematNavBarObj,
      ]);
    if (dematAccountFound !== undefined && dematAccountFound !== null) {
      // rejected demat document -- leave step incomplete, handled by caller
    } else completeModuleStatus(dashboardUrl.demat);
  }
  if (GlobalStore.stageStatus.isesigncompleted)
    completeModuleStatus(dashboardUrl.eSign);
}

/**
 * Marks a single navbar step incomplete without touching any other step.
 * Used for the "rejected document found" overrides (demat / address reject)
 * that WelcomeBack and StatusContinue both apply after checkModuleCompleteness.
 */
export function markStepIncomplete(navUrl: string): void {
  const moduleArr = GlobalStore.leftNavBarState.map((record) =>
    record.navUrl === navUrl ? { ...record, isComplete: false } : record
  );
  GlobalStore.setLeftNavBarState(moduleArr);
}

/** Marks every navbar stage complete except the last one (by priority). */
export function completeAllStagesExceptLast(): void {
  const sorted = [...GlobalStore.leftNavBarState].sort(
    (a, b) => a.priority - b.priority
  );
  if (sorted.length === 0) return;
  const lastNavUrl = sorted[sorted.length - 1].navUrl;
  const moduleArr = GlobalStore.leftNavBarState.map((record) =>
    record.navUrl === lastNavUrl ? { ...record } : { ...record, isComplete: true }
  );
  GlobalStore.setLeftNavBarState(moduleArr);
}

/**
 * Equity / RM-assisted PAN-flow navbar shape: drop Profiler + Plan (they
 * don't apply to equity/DP-only accounts), ensure the Demat tile is present.
 *
 * BUGFIX: legacy PANInfo.tsx call sites built this off `leftNavBarArray`
 * directly (`let filterLeftBar = leftNavBarArray;` then `.splice(...)`),
 * mutating the shared pristine constant. This version always starts from a
 * fresh copy, and uses .filter() (order-independent -- ProfileNavBar always
 * re-sorts by `priority` before rendering) instead of index-based splices,
 * which also removes a class of "stale index" bugs the splice version had.
 */
export function applyEquityNavbarLayout(): void {
  let next: Array<ILeftNavBarArray> = [...leftNavBarArray].filter(
    (item) => item.navUrl !== dashboardUrl.profiler && item.navUrl !== "/plan"
  );
  if (!next.some((item) => item.navUrl === dematNavBarObj.navUrl)) {
    next = [...next, dematNavBarObj];
  }
  GlobalStore.setLeftNavBarState(next);
}

/**
 * RM-assisted / "skip URL" PAN-flow navbar shape: drop Plan, E-Sign and
 * Profiler, ensure Demat is present. Used regardless of whether the PAN is
 * Aadhaar-linked (both legacy branches converged on the same intended
 * result set once the stale-index bug below is fixed).
 *
 * BUGFIX: the legacy "Aadhaar not linked" branch re-searched for the
 * already-removed Profiler tile a second time (getting index -1) but
 * guarded the splice with a stale `filterNavIndex > 0` from an earlier
 * search, so it executed `splice(-1, 1)` and silently deleted whatever
 * tile happened to be last in the array (usually "Verify Personal
 * Documents"). Filtering by navUrl membership removes this class of bug.
 */
export function applyRmSkipNavbarLayout(): void {
  const dropUrls = new Set([
    "/plan",
    dashboardUrl.eSign,
    dashboardUrl.profiler,
  ]);
  let next: Array<ILeftNavBarArray> = [...leftNavBarArray].filter(
    (item) => !dropUrls.has(item.navUrl)
  );
  if (!next.some((item) => item.navUrl === dematNavBarObj.navUrl)) {
    next = [...next, dematNavBarObj];
  }
  GlobalStore.setLeftNavBarState(next);
}

/**
 * Valid-KRA + equity-account navbar shape: drop E-Sign, ensure
 * "Authenticate & Submit" tile is present.
 */
export function applyKraAuthenticateNavbarLayout(): void {
  let next: Array<ILeftNavBarArray> = GlobalStore.leftNavBarState.filter(
    (item) => item.navUrl !== dashboardUrl.eSign
  );
  if (!next.some((item) => item.navUrl === authenticateNavBarObj.navUrl)) {
    next = [...next, authenticateNavBarObj];
  }
  GlobalStore.setLeftNavBarState(next);
}

/* ============================================================================
 * SECTION 2 - CACHE + KRA
 *
 * Contract (see WelcomeBack.CacheFunction / KraCheckApi / kraDobCheckNew):
 *   1. Always call Cache first.
 *   2. If cache has valid tagging data -> use it (TaggingMode 1 = KRA,
 *      2 = DigiLocker, 3 = normal client with cached address). Do NOT call
 *      the live KRA API in this case.
 *   3. If cache has no valid tagging data -> call the live KRA API
 *      (only when we have a dob + pan to check).
 *   4. If KRA has valid data (AppStatus "07", or NONINDIVIDUAL/NRI + "02") -> use it.
 *   5. If neither cache nor KRA have usable data -> fall back to the
 *      caller-supplied "no data at all" handler (the existing normal flow).
 * ==========================================================================*/

/** dd/mm/yyyy -> mm-dd-yyyy, matching the legacy WelcomeBack/StatusContinue helper. */
function convertDateForKraRequest(dateString: string): string {
  const [day, month, year] = dateString.split("/");
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return `${(date.getMonth() + 1).toString().padStart(2, "0")}-${day.padStart(
    2,
    "0"
  )}-${year}`;
}

export interface CacheResult {
  taggingMode?: string;
  data?: any;
}

/** Parses the raw Cache/Get response into a simple shape, or null if no usable tagging is present. */
export function extractCacheResult(res: any): CacheResult | null {
  if (res && res.data && res.data.Result && res.data.Result.IsTaggingPresent) {
    const result = res.data.Result;
    if (result !== null && result.IsTaggingPresent) {
      return { taggingMode: result.TaggingMode, data: result.Data };
    }
  }
  return null;
}

/** True when neither personal nor profile details are marked complete on GlobalStore.stageStatus. */
export function isProfileDataIncomplete(): boolean {
  return (
    GlobalStore.stageStatus.ispersonaldetailscompleted !== true &&
    GlobalStore.stageStatus.isprofiledetailscompleted !== true
  );
}

/**
 * True when a live KRA response should be treated as a valid KRA match.
 * entityType should be GlobalStore.entityType (matches WelcomeBack exactly).
 * NRI accounts (NRI INV and NRI OTC) come back from KRA as "02" or "07",
 * so "02" is accepted for them as well as for NONINDIVIDUAL.
 */
export function isKraResponseValid(responseObj: any, entityType?: string): boolean {
  const isNri = !!(GlobalStore.isNriInv || GlobalStore.isNriOtc);
  return !!(
    responseObj &&
    (responseObj.AppStatus === "07" ||
      ((entityType === "NONINDIVIDUAL" || isNri) && responseObj.AppStatus === "02"))
  );
}

/**
 * Applies a valid KRA response (from cache TaggingMode "1" or a live
 * GetKraDetails call) onto GlobalStore + the clientPartialDetails_ao
 * localStorage blob. Ported verbatim from WelcomeBack's
 * cacheKRAFunction/kraDobCheckNew (the two were near-identical; the only
 * difference was that the cache path additionally persisted Gender, kept
 * here as `options.setGender`).
 */
export function applyKraResponseToStore(
  responseObj: any,
  options?: { setGender?: boolean }
): any {
  GlobalStore.isKRA = true;
  const clientPartialDetails_ao = JSON.parse(
    window.localStorage.getItem("clientPartialDetails_ao") || "{}"
  );
  GlobalStore.KRA_email = responseObj.KraEmail ? responseObj.KraEmail : "";
  GlobalStore.KRA_mobile = responseObj.KraMobile ? responseObj.KraMobile : "";
  GlobalStore.KRA_mode = responseObj.kra_mode ? responseObj.kra_mode : 0;

  const resultObj = responseObj;
  GlobalStore.kraInfoObject.addressLine1 = `${resultObj.AppPerAdd1} ${resultObj.AppPerAdd2 || ""
    } ${resultObj.AppPerAdd3 || ""}`;
  GlobalStore.kraInfoObject.corrAddress = `${resultObj.AppCorAdd1} ${resultObj.AppCorAdd2 || ""
    } ${resultObj.AppCorAdd3 || ""}`;
  GlobalStore.kraInfoObject.pincode = resultObj.AppPerPincd;
  GlobalStore.kraInfoObject.corrPincode = resultObj.AppCorPincd;
  NriAddressStore.setFromKra(
    resultObj,
    GlobalStore.personaldetails?.pan || clientPartialDetails_ao.pan
  );
  GlobalStore.personaldetails.fsfullName = resultObj.FatherName
    ? resultObj.FatherName
    : GlobalStore.personaldetails.fsfullName;

  clientPartialDetails_ao.isKRA = GlobalStore.isKRA;
  if (options?.setGender) {
    clientPartialDetails_ao.gender = responseObj?.Gender;
    GlobalStore.personaldetails.gender = responseObj?.Gender;
  }
  clientPartialDetails_ao.addressLine1 = GlobalStore.kraInfoObject.addressLine1;
  clientPartialDetails_ao.PinCode = GlobalStore.kraInfoObject.pincode;
  clientPartialDetails_ao.corrAddress = GlobalStore.kraInfoObject.corrAddress;
  clientPartialDetails_ao.corrPincode = GlobalStore.kraInfoObject.corrPincode;
  clientPartialDetails_ao.fsfullName = GlobalStore.personaldetails.fsfullName;
  clientPartialDetails_ao.sameAsPermanent =
    GlobalStore.kraInfoObject.addressLine1 == GlobalStore.kraInfoObject.corrAddress;

  window.localStorage.setItem(
    "clientPartialDetails_ao",
    JSON.stringify(clientPartialDetails_ao)
  );
  return clientPartialDetails_ao;
}

export interface CacheThenKraOptions {
  applicationID: string;
  /** Raw dob string in dd/mm/yyyy form (e.g. responseObj.dob from GetStatusV1). */
  dob?: string;
  pan?: string;
  clientPrimaryDetailId?: string | number;
  leadId?: string;
  createdBy?: number;
  /** GlobalStore.entityType -- required for the NONINDIVIDUAL + AppStatus "02" KRA-valid rule. */
  entityType?: string;
  /** Cache TaggingMode "1": treat as KRA match. */
  onKraFromCache: (cacheData: any) => void;
  /** Cache TaggingMode "2": DigiLocker tagging. */
  onDigiLockerFromCache: (cacheData: any) => void;
  /** Cache TaggingMode "3": normal client with cached address. */
  onNormalClientFromCache: (cacheData: any) => void;
  /** Live KRA call succeeded (AppStatus "07", or "02" for NONINDIVIDUAL/NRI). GlobalStore is already updated when this fires. */
  onKraValid: (kraResponseObj: any) => void;
  /** Live KRA call completed but did not match -> normal client / DigiLocker fallback decision is the caller's. */
  onKraInvalid: () => void;
  /** Live KRA call errored, or returned "INVALID DOB". */
  onKraError?: (res: any) => void;
  /**
   * Called when there is no cache tagging AND no dob/pan to check against
   * KRA. This is the "existing normal flow" fallback required when both
   * Cache and KRA are unavailable.
   */
  onNoKraFallback: () => void;
}

function checkLiveKraDetails(options: CacheThenKraOptions): void {
  const reqObj = {
    dbo: `${moment(convertDateForKraRequest(options.dob as string), "MM-DD-YYYY").format(
      "DD/MM/YYYY"
    )}`,
    panno: options.pan,
    applicationId: options.applicationID,
    clientPrimaryDetailId: options.clientPrimaryDetailId || 0,
    leadId: options.leadId,
    CreatedBy: options.createdBy || 0,
  };
  AGetKraDetails((res: any) => {
    if (!res || res.error) {
      options.onKraError?.(res);
      return;
    }
    const responseObj = res.data;
    GlobalStore.IsDigiLockerDirectFlow = responseObj?.IsDigiLocker;
    localStorage.setItem(
      "IsDigiLockerDirectFlow",
      responseObj?.IsDigiLocker?.toString()
    );
    if (responseObj?.AppErrorDesc?.includes("INVALID DOB")) {
      options.onKraError?.(res);
      return;
    }
    const inner = res.data ? res.data : res;
    if (isKraResponseValid(inner, options.entityType)) {
      applyKraResponseToStore(inner);
      options.onKraValid(inner);
    } else {
      options.onKraInvalid();
    }
  }, reqObj);
}

function runKraFallbackOrNoDataFallback(options: CacheThenKraOptions): void {
  if (options.dob && options.pan) {
    checkLiveKraDetails(options);
  } else {
    options.onNoKraFallback();
  }
}

/**
 * The complete Cache -> KRA -> fallback cascade. See the SECTION 2 header
 * comment for the exact contract. This is the single function that should
 * be called anywhere the app needs to resolve "does this client already
 * have KRA/KYC/DigiLocker data we can reuse" (WelcomeBack Continue flow,
 * StatusContinue reload flow, PANInfo post-PAN-submission flow).
 */
export function resolveCacheThenKra(options: CacheThenKraOptions): void {
  AGetCacheData(
    (res: any) => {
      const cacheResult = extractCacheResult(res);
      if (cacheResult) {
        // NOTE: intentionally no "else" here for the outer gate below -- if
        // applicationID is missing or profile/personal data is already
        // complete, the legacy code (WelcomeBack.CacheFunction) does
        // nothing at all in this branch. Preserved as-is.
        if (
          options.applicationID &&
          isProfileDataIncomplete() &&
          cacheResult.data !== null
        ) {
          if (cacheResult.taggingMode == "1") {
            options.onKraFromCache(cacheResult.data);
          } else if (cacheResult.taggingMode == "2") {
            options.onDigiLockerFromCache(cacheResult.data);
          } else if (cacheResult.taggingMode == "3") {
            options.onNormalClientFromCache(cacheResult.data);
          } else {
            runKraFallbackOrNoDataFallback(options);
          }
        }
      } else {
        runKraFallbackOrNoDataFallback(options);
      }
    },
    {
      uniqueID: options.applicationID,
      key: `AOPDROPOFF-${options.applicationID}`,
    }
  );
}

/* ============================================================================
 * SECTION 3 - STATUS (getStatus) DRIVEN NAVIGATION
 * ==========================================================================*/

export interface StatusNavigationContext {
  userId: string;
  isUploadDocumentsCompleted: boolean;
  /**
   * Fired for the "profile or personal details incomplete" branch, which is
   * where WelcomeBack hands off to Cache -> KRA resolution instead of
   * navigating directly. The caller is responsible for invoking
   * resolveCacheThenKra() (and navigating) from inside this callback.
   */
  onNeedsCacheAndKra: () => void;
}

/**
 * Ports WelcomeBack's `getClientStageDetails` main routing ladder
 * (the ordered if/return chain evaluated after setStageData +
 * checkModuleCompleteness) as a pure decision function. Returns the URL to
 * navigate to, or null when the decision was already handed off via
 * `context.onNeedsCacheAndKra` or when no rule matched (matches the
 * original's implicit no-op fall-through).
 */
export function resolveStatusNavigationUrl(
  responseObj: any,
  context: StatusNavigationContext
): string | null {
  if (
    !responseObj.isdigilockercompleted &&
    GlobalStore.aadharLinkedChoice &&
    !GlobalStore.isDigiLocker &&
    !GlobalStore.isKRA
  ) {
    return containerUrl.dgLocker;
  }

  if (
    !(
      responseObj.isprofiledetailscompleted &&
      responseObj.ispersonaldetailscompleted
    )
  ) {
    context.onNeedsCacheAndKra();
    return null;
  }

  if (!responseObj.isbankdetailscompleted) {
    return dashboardUrl.bank;
  }

  if (responseObj?.accountcategory === 2 && !responseObj?.isdpcompleted) {
    return dashboardUrl.demat;
  }

  if (
    responseObj?.accountcategory !== 2 &&
    (!responseObj.isbrokeragesegmentcompleted ||
      !responseObj.isbrokerageschemecompleted)
  ) {
    return dashboardUrl.plan;
  }

  if (!context.isUploadDocumentsCompleted) {
    return dashboardUrl.doc;
  }

  if (
    !GlobalStore.isDigiLocker &&
    !GlobalStore.isKRA &&
    !GlobalStore.isKYC &&
    !responseObj.isipvarificationcompleted
  ) {
    return dashboardUrl.ipv;
  }

  if (responseObj.isagentvisitcompleted) {
    return containerUrl.ocv;
  }

  if (
    responseObj.iskra !== null &&
    responseObj.iskra !== undefined &&
    responseObj.iskra &&
    responseObj?.accountcategory === 2
  ) {
    if (
      responseObj.bpm_pd_status === 1 &&
      responseObj.applicationstatus === 1 &&
      responseObj?.auth_ver
    ) {
      return containerUrl.submit;
    }
    if (
      context.userId !== "0" &&
      responseObj.bpm_pd_status === 0 &&
      responseObj.applicationstatus === 0
    ) {
      return dashboardUrl.doc;
    }
    return dashboardUrl.authenticate;
  }

  if (!responseObj.isesigncompleted) {
    return dashboardUrl.eSign + "?sc=e";
  }

  if (responseObj.isesigncompleted && !responseObj?.second_esign_req) {
    return containerUrl.submit;
  }

  if (responseObj?.isesigncompleted && responseObj?.accountcategory === 2) {
    return containerUrl.submit;
  }

  if (responseObj.isesigncompleted && !responseObj.iskraesigncompleted) {
    return dashboardUrl.eSign + "?sc=k";
  }

  if (responseObj.bpm_pd_status === 0) {
    return statusUrl.docVerificationInProgress;
  }

  if (responseObj.bpm_pd_status === 1) {
    return containerUrl.poa;
  }

  return null;
}

/**
 * Canonical "where should an existing application with an incomplete
 * profile/personal stage land" rule, used when a page needs this answer
 * synchronously (i.e. without going through the full Cache/KRA cascade --
 * e.g. PANInfo's post-getStatus `nextIncompleteStage` computation).
 *
 * This mirrors WelcomeBack's own DigiLocker guard
 * (`!isdigilockercompleted && aadharLinkedChoice && !isDigiLocker && !isKRA -> dgLocker`)
 * combined with its Cache-miss callback rule
 * (`isaddressstagecompleted === false && !isotc -> personalIfo, else -> profile`).
 *
 * BUGFIX: PANInfo.tsx previously had two different, disagreeing versions of
 * this rule (`getClientStageDetails` always returned `profile`;
 * `getClientStageDetailsNew` had the fuller dgLocker/personalIfo/profile
 * rule below). Both now use this single function.
 */
export function getIncompleteProfileNavigationUrl(responseObj: any): string {
  if (
    !responseObj.iskra &&
    responseObj?.accountcategory !== 2 &&
    !responseObj?.isotc
  ) {
    return containerUrl.dgLocker;
  }
  if (responseObj?.isaddressstagecompleted === false && !responseObj?.isotc) {
    return dashboardUrl.personalIfo;
  }
  return dashboardUrl.profile;
}

/* ============================================================================
 * SECTION 4 - "MODIFY" PAN-FLOW RESET (Lead -> AResetAccount -> 5s delay)
 *
 * Contract (see PANInfo.tsx `/pan?...&modify=true`):
 *   1. When the PAN page URL carries `modify=true`, the normal PAN-info page
 *      actions must NOT run immediately. Instead: fetch the lead via the
 *      existing External Lead API (ALeadFetchApi), use its response to build
 *      the AResetAccount request, call AResetAccount, and only once that
 *      succeeds wait exactly 5 seconds before resuming the existing flow.
 *   2. If either call fails, the caller's onError is invoked and the normal
 *      flow must NOT run.
 * This keeps the one-off delay/sequencing isolated here instead of scattering
 * ad-hoc setTimeouts through PANInfo.tsx.
 * ==========================================================================*/

const MODIFY_RESET_RESUME_DELAY_MS = 5000;

export interface ModifyResetFlowOptions {
  id: string;
  name: string;
  /** Called exactly once AResetAccount succeeds and the 5s wait has elapsed. */
  onSuccess: () => void;
  /** Called when either the External Lead fetch or AResetAccount fails. */
  onError: (error: any) => void;
}

/**
 * Runs the External Lead -> AResetAccount -> 5s-delay sequence required by
 * `/pan?...&modify=true` before the existing PAN flow is allowed to resume.
 * Reuses the existing ALeadFetchApi / AResetAccount services as-is (same
 * request shapes used elsewhere in the app) -- no duplicated API logic.
 */
export function runModifyResetFlow(options: ModifyResetFlowOptions): void {
  ALeadFetchApi(
    (res: any) => {
      if (!res || res.error || !Array.isArray(res?.data) || res.data.length === 0) {
        options.onError(res);
        return;
      }
      const leadData = res.data[0];
      const resetReq = {
        Applicationid:
          leadData?.accountCategory === "2" || leadData?.accountCategory === 2
            ? `${leadData?.applicationId}I`
            : leadData?.applicationId,

        Accountcategory:
          leadData?.accountCategory !== null &&
            leadData?.accountCategory !== undefined &&
            leadData?.accountCategory !== ""
            ? parseInt(leadData.accountCategory)
            : undefined,

        Createdby: 0,
      };
      AResetAccount((resetRes: any) => {
        if (!resetRes || resetRes.error) {
          options.onError(resetRes);
          return;
        }
        setTimeout(() => {
          options.onSuccess();
        }, MODIFY_RESET_RESUME_DELAY_MS);
      }, resetReq);
    },
    {
      id: encodeURIComponent(options.id),
      name: encodeURIComponent(options.name),
      stageFlag: 1,
    }
  );
}
